import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { LoadingSpinner } from "../Spinner";
import "./FitnessProfileBannerModal.css";

const STORAGE_KEY = "ratafit_fitness_banner_dismissed";

export function getFitnessBannerDismissed() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export function setFitnessBannerDismissed() {
  try {
    localStorage.setItem(STORAGE_KEY, "true");
  } catch (_) {}
}

export default function FitnessProfileBannerModal({
  visible,
  onClose,
  onDontShowAgain,
  userId,
}) {
  const [form, setForm] = useState({
    age: "",
    sex: "",
    weightKg: "",
    heightCm: "",
    bodyType: "",
    experienceYears: "",
    goals: "",
    healthNotes: "",
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!visible || !userId) return;
    setLoading(true);
    const load = async () => {
      try {
        const userRef = doc(db, "users", userId);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const fp = snap.data().fitnessProfile || {};
          setForm({
            age: fp.age != null ? String(fp.age) : "",
            sex: fp.sex || "",
            weightKg: fp.weightKg != null ? String(fp.weightKg) : "",
            heightCm: fp.heightCm != null ? String(fp.heightCm) : "",
            bodyType: fp.bodyType || "",
            experienceYears: fp.experienceYears != null ? String(fp.experienceYears) : "",
            goals: fp.goals || "",
            healthNotes: fp.healthNotes || "",
          });
        }
      } catch (_) {}
      setLoading(false);
    };
    load();
  }, [visible, userId]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const fp = {
        age: form.age.trim() ? parseInt(form.age.trim(), 10) : null,
        sex: (form.sex || "").trim() || null,
        weightKg: form.weightKg.trim() ? parseFloat(form.weightKg.trim()) : null,
        heightCm: form.heightCm.trim() ? parseInt(form.heightCm.trim(), 10) : null,
        bodyType: (form.bodyType || "").trim() || null,
        experienceYears: form.experienceYears.trim() ? parseFloat(form.experienceYears.trim()) : null,
        goals: (form.goals || "").trim().slice(0, 500) || null,
        healthNotes: (form.healthNotes || "").trim().slice(0, 500) || null,
      };
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { fitnessProfile: fp, updatedAt: serverTimestamp() });
      onClose();
    } catch (_) {
      setSaving(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDontShowAgain = () => {
    setFitnessBannerDismissed();
    onDontShowAgain?.();
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="fpb-modal-overlay" onClick={onClose}>
      <div className="fpb-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="fpb-close" onClick={onClose} aria-label="Cerrar">
          ✕
        </button>
        <div className="fpb-header">
          <span className="fpb-icon">🤖</span>
          <h2 className="fpb-title">Mejorá tus rutinas y dietas con IA</h2>
          <p className="fpb-message">
            Si completás tu perfil físico, el asistente puede personalizar mejor las rutinas y dietas según tu edad, peso, experiencia y objetivos. Solo te toma un momento y podés editarlo cuando quieras en Mi perfil.
          </p>
        </div>
        {loading ? (
          <LoadingSpinner text="Cargando…" />
        ) : (
          <div className="fpb-form">
            <div className="fpb-row">
              <div className="fpb-group">
                <label className="fpb-label">Edad</label>
                <input
                  type="number"
                  min="10"
                  max="120"
                  className="fpb-input"
                  placeholder="ej. 35"
                  value={form.age}
                  onChange={(e) => updateField("age", e.target.value)}
                />
              </div>
              <div className="fpb-group">
                <label className="fpb-label">Sexo</label>
                <select
                  className="fpb-input"
                  value={form.sex}
                  onChange={(e) => updateField("sex", e.target.value)}
                >
                  <option value="">No especificado</option>
                  <option value="hombre">Hombre</option>
                  <option value="mujer">Mujer</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
            <div className="fpb-row">
              <div className="fpb-group">
                <label className="fpb-label">Peso (kg)</label>
                <input
                  type="number"
                  min="30"
                  max="300"
                  step="0.5"
                  className="fpb-input"
                  placeholder="ej. 80"
                  value={form.weightKg}
                  onChange={(e) => updateField("weightKg", e.target.value)}
                />
              </div>
              <div className="fpb-group">
                <label className="fpb-label">Altura (cm)</label>
                <input
                  type="number"
                  min="100"
                  max="250"
                  className="fpb-input"
                  placeholder="ej. 175"
                  value={form.heightCm}
                  onChange={(e) => updateField("heightCm", e.target.value)}
                />
              </div>
            </div>
            <div className="fpb-group">
              <label className="fpb-label">Tipo de cuerpo</label>
              <select
                className="fpb-input"
                value={form.bodyType}
                onChange={(e) => updateField("bodyType", e.target.value)}
              >
                <option value="">No especificado</option>
                <option value="ectomorfo">Ectomorfo</option>
                <option value="mesomorfo">Mesomorfo</option>
                <option value="endomorfo">Endomorfo</option>
              </select>
            </div>
            <div className="fpb-group">
              <label className="fpb-label">Años entrenando</label>
              <input
                type="number"
                min="0"
                max="50"
                step="0.5"
                className="fpb-input"
                placeholder="ej. 2"
                value={form.experienceYears}
                onChange={(e) => updateField("experienceYears", e.target.value)}
              />
            </div>
            <div className="fpb-group">
              <label className="fpb-label">Objetivos (opcional)</label>
              <input
                type="text"
                className="fpb-input"
                placeholder="ej. Ganar masa, definir"
                value={form.goals}
                onChange={(e) => updateField("goals", e.target.value)}
                maxLength={500}
              />
            </div>
            <div className="fpb-group">
              <label className="fpb-label">Notas de salud (opcional)</label>
              <textarea
                className="fpb-input fpb-textarea"
                placeholder="ej. Rodilla derecha"
                value={form.healthNotes}
                onChange={(e) => updateField("healthNotes", e.target.value)}
                maxLength={500}
                rows={2}
              />
            </div>
            <div className="fpb-actions">
              <button
                type="button"
                className="fpb-btn fpb-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Guardando…" : "Guardar y cerrar"}
              </button>
              <button
                type="button"
                className="fpb-btn fpb-btn-secondary"
                onClick={handleDontShowAgain}
              >
                No volver a mostrar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
